import React, { useState, useEffect, useContext } from 'react';
import {
  params,
  extend,
  string as stringType,
  useLazyDataModel,
  customType
} from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import proptypes from 'prop-types';
import { More } from '@one-thd/sui-icons';
import {
  IconButton, Menu, MenuItem, Button, ButtonGroup, Modal,
  ModalHeader, ModalBody, ModalFooter, Typography
} from '@one-thd/sui-atomic-components';
import {
  ACTIVE,
  ARCHIVE,
  ARCHIVED,
  SET_ACTIVE,
  PROJECT_STATUS_CHANGE,
  PROJECT_STATUS_OVERLAY,
  PROJECT_STATUS_MENU_CLICK,
  DELETE,
  MODAL_INFO_ARRAY,
} from '../constants';
import { storeStatusChange, triggerAnalytics } from '../helpers';
import { ProHouseAccountWarning } from './ProHouseAccountWarning';

const { string, func, bool } = proptypes;

export const MoreButton = ({
  projectStatus,
  projectId,
  projectName,
  onDelete,
  onStatusChangeSuccess,
  isProHouseProject = false
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({});
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const statusLabel = projectStatus === ACTIVE ? ARCHIVE : SET_ACTIVE;

  const handleOpenMenu = (evt) => {
    setAnchorEl(evt.currentTarget);
    setOpenMenu(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const handleOpenStatusModal = () => {
    handleCloseMenu();
    if (projectStatus === ACTIVE) {
      const onClickText = 'projects: projects page: meatball: archive';
      triggerAnalytics(PROJECT_STATUS_MENU_CLICK, { settings: onClickText });
      setModalInfo(MODAL_INFO_ARRAY[1]);
      const overlayText = 'projects: archive project';
      triggerAnalytics(PROJECT_STATUS_OVERLAY, { overlay: overlayText });
    } else {
      const onClickText = 'projects: projects page: meatball: set active';
      triggerAnalytics(PROJECT_STATUS_MENU_CLICK, { settings: onClickText });
      const overlayText = 'projects: set active project';
      triggerAnalytics(PROJECT_STATUS_OVERLAY, { overlay: overlayText });
      setModalInfo(MODAL_INFO_ARRAY[2]);
    }
    setOpenModal(true);
  };

  const handleOpenDeleteModal = () => {
    const onClickText = 'projects: projects page: meatball: delete';
    triggerAnalytics(PROJECT_STATUS_MENU_CLICK, { settings: onClickText });
    handleCloseMenu();
    setModalInfo(MODAL_INFO_ARRAY[0]);
    setOpenModal(true);
    const overlayText = 'projects: delete project';
    triggerAnalytics(PROJECT_STATUS_OVERLAY, { overlay: overlayText });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const [updateProject, updateProjectResponse] = useLazyDataModel('updateProject', {
    variables: {
      projectId,
      operationType: 'STATUS'
    },
    context: { withAuth: true }
  });

  const handleDelete = async (submitEvent) => {
    submitEvent.preventDefault();

    if (projectId) {
      await updateProject({
        variables: {
          statusType: 'DELETE'
        }
      });
    }
  };

  const handleChangeStatus = async (submitEvent) => {
    submitEvent.preventDefault();

    if (projectId && projectStatus === ACTIVE) {
      await updateProject({
        variables: {
          statusType: 'ARCHIVED'
        }
      });
    }
    if (projectId && projectStatus === ARCHIVED) {
      await updateProject({
        variables: {
          statusType: 'ACTIVE'
        }
      });
    }
  };

  const urlPathname = typeof window !== 'undefined' ? window.location?.pathname : '';

  useEffect(() => {
    if (updateProjectResponse?.data?.updateProject && updateProjectResponse?.data?.updateProject.id) {
      setOpenModal(false);
      const priorStatus = projectStatus === ACTIVE ? ACTIVE : ARCHIVED;
      const updatedStatus = updateProjectResponse?.data?.updateProject.status;
      let statusChangeString;
      if (updatedStatus === 'deleted') {
        statusChangeString = 'deletion';
      } else if (updatedStatus === ACTIVE) {
        statusChangeString = 'set active';
      } else {
        statusChangeString = 'archive';
      }

      // In the format of “projects: state: <prior state>: successful <action>”
      // where <prior state> is the prior status of a project and <action> is the new status
      const settingsString = `projects: state: ${priorStatus}: successful ${statusChangeString}`;
      triggerAnalytics(PROJECT_STATUS_CHANGE, { settings: settingsString });

      if (urlPathname === `/projects/${projectId}`) {
        if (updateProjectResponse?.data?.updateProject.status === 'deleted') {
          storeStatusChange(updateProjectResponse?.data?.updateProject.status);
          onDelete();
        } else {
          onStatusChangeSuccess({ projectStatus });
        }
      } else {
        storeStatusChange(updateProjectResponse?.data?.updateProject.status);
        window.location.reload();
      }
    }
  }, [updateProjectResponse?.data, updateProjectResponse?.error]);

  const isDeleteAction = modalInfo.type === DELETE;

  return (
    <>
      <IconButton
        icon={More}
        aria-label="More Button"
        onClick={handleOpenMenu}
        className="sui-mt-1"
      />
      <Menu
        id="moreButton popover"
        open={openMenu}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        MenuListProps={{ 'aria-labelledby': 'Menu-Button' }}
      >
        <div className="sui-w-28">
          <MenuItem data-testid="menu-status-button" onClick={handleOpenStatusModal}>{statusLabel}</MenuItem>
          <MenuItem data-testid="menu-delete-button" onClick={handleOpenDeleteModal}>Delete</MenuItem>
        </div>
      </Menu>
      {
        isDeleteAction && isProHouseProject ? (
          <ProHouseAccountWarning handleCloseModal={handleCloseModal} openModal={openModal} />
        ) : (
          <Modal open={openModal} onClose={handleCloseModal} size="sm">
            <ModalHeader onClose={handleCloseModal}>
              {modalInfo.header}
            </ModalHeader>
            <ModalBody>
              <div className="sui-grid sui-gap-4">
                <div className="sui-flex sui-flex-wrap sui-font-bold">
                  <Typography variant="body-xs">{modalInfo.query}&nbsp;</Typography>
                  {
                    modalInfo.type === ARCHIVE && (
                      <div className="sui-flex">
                        <Typography variant="body-xs" weight="bold">{projectName}</Typography>
                        <Typography variant="body-xs">?</Typography>
                      </div>
                    )
                  }
                </div>
                <Typography variant="body-xs">
                  {modalInfo.warning}
                </Typography>
              </div>
            </ModalBody>
            <ModalFooter position="end">
              {
                !isMobile ? (
                  <ButtonGroup>
                    <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                    <Button variant="primary" onClick={isDeleteAction ? handleDelete : handleChangeStatus}>
                      {modalInfo.button}
                    </Button>
                  </ButtonGroup>
                ) : (
                  <ButtonGroup>
                    <Button variant="primary" onClick={isDeleteAction ? handleDelete : handleChangeStatus}>
                      {modalInfo.button}
                    </Button>
                    <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                  </ButtonGroup>
                )
              }
            </ModalFooter>
          </Modal>
        )
      }
    </>
  );
};

MoreButton.displayName = 'MoreButton';

MoreButton.propTypes = {
  projectId: string.isRequired,
  projectName: string.isRequired,
  projectStatus: string,
  onDelete: func,
  onStatusChangeSuccess: func,
  isProHouseProject: bool
};

MoreButton.defaultProps = {
  projectStatus: null,
  onDelete: null,
  onStatusChangeSuccess: null,
  isProHouseProject: false
};

MoreButton.dataModel = extend({
  updateProject: params({
    projectId: stringType().isRequired(),
    operationType: customType('OperationType').enum(['STATUS', 'INFO', 'ADDRESS']).isRequired(),
    statusType: customType('StatusType').enum(['ACTIVE', 'ARCHIVED', 'DELETE'])
  }).mutation().shape({
    id: stringType(),
    status: stringType()
  })
});
